import {
  Box,
  Button,
  Container,
  CssBaseline,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAppDispatch } from "../../app/features/store/store";
import { forgotUserPassword } from "../../app/features/user/userSlice";
import getAxiosError from "../../app/api/axiosError";
import { toast } from "react-toastify";
import Loader from "../../components/common/loader/Loader";
import InfoIcon from "@mui/icons-material/Info";
import { useState } from "react";
import "./ForgotPassword.css";
import { Copyright } from "../login/Login";

const ForgotPassword = () => {
  const dispatch = useAppDispatch();
  const [showInfo, setShowInfo] = useState<boolean>(false);

  const validationSchema = Yup.object({
    username: Yup.string().required("Username is required"),
  });

  const formik = useFormik({
    initialValues: {
      username: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const username = values.username;
        const resultAction = await dispatch(
          forgotUserPassword(username)
        ).unwrap();
        if (resultAction.isSuccess) {
          setShowInfo(true);
          formik.values.username = "";
          toast.success(resultAction.message!, {
            toastId: "reset-password-success",
          });
        }
        if (!resultAction.isSuccess) {
          toast.error(resultAction.message!, {
            toastId: "reset-password-success",
          });
        }
      } catch (error) {
        const appError = getAxiosError(error);
        toast.error(appError.errorMessage, { toastId: "reset-password-error" });
      }
    },
  });

  return (
    <>
      <div className="pageImageStyle">
        <Box
          sx={{
            margin: 0,
            padding: 0,
            textDecoration: "none",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "calc(10px + 2vmin)",
            position: "relative",
          }}
        >
          <Paper
            elevation={10}
            sx={{
              mt: 0,
              mb: 3,
            }}
          >
            <Container component="main" maxWidth="xs">
              <CssBaseline />
              <Box
                sx={{
                  marginTop: 3,
                  mb: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography variant="h4">Forgot Password</Typography>
                <Box sx={{ mt: 2, mb: 4, width: "359px" }}>
                  <form onSubmit={formik.handleSubmit}>
                    <TextField
                      fullWidth
                      id="username"
                      name="username"
                      label="Username"
                      variant="standard"
                      size="medium"
                      value={formik.values.username}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.username &&
                        Boolean(formik.errors.username)
                      }
                      helperText={
                        formik.touched.username && formik.errors.username
                      }
                    />
                    <Button
                      sx={{ mt: 2, textTransform: "none", fontSize: 14 }}
                      variant="contained"
                      size="medium"
                      fullWidth
                      type="submit"
                      disabled={!formik.isValid || formik.isSubmitting}
                    >
                      Submit
                    </Button>
                  </form>
                </Box>
                {showInfo && (
                  <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                    <InfoIcon sx={{ marginRight: 1 }} />
                    <Typography color={"#0d3b15"} fontWeight={600}>
                      A password reset email has been sent to your registered
                      email address. Please check your inbox.
                    </Typography>
                  </Box>
                )}
                <Link href="/" sx={{ cursor: "pointer" }} variant="body1">
                  Return To Login
                </Link>
              </Box>
              <Copyright sx={{ mt: 3, mb: 4 }} />
            </Container>
          </Paper>
        </Box>
        <Loader isLoading={formik.isSubmitting} />
      </div>
    </>
  );
};

export default ForgotPassword;
